/**
 * Settings pocket — the compact drill-down panel that matches the tray
 * (spec §4). One stack of pages: the header goes back one level; Esc first
 * restores an edited field (the field consumes it), then goes back, and
 * closes at the root. Desktop places it through the settings frame store;
 * the web hangs it below the top bar.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { PocketHeader, PocketPanel, PocketRowWrapScope } from "@/components/ui";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { ModalSurfaceScope } from "@/components/ui/modal-surface-context";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { useDraggableModal } from "@/hooks/use-draggable-modal";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import { useT } from "@/i18n";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";
import { beginSettingsFrameDrag } from "@/desktop/settings-frame-drag";
import { captureSettingsFramePreview } from "@/desktop/settings-frame-preview";
import { useSettingsFrameSurface } from "@/desktop/use-settings-frame-surface";
import { acknowledgeBrowserSettingsYield } from "@/desktop/browser-connection-yield";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { requestNativeGlassRefresh } from "@/desktop/use-native-glass-surfaces";
import { restoreAndFocusCurrentWindow } from "@/desktop/use-global-hotkey";
import { SettingsDraftProvider } from "./settings-draft-provider";
import {
  SettingsLeaveGuardContext,
  SettingsNavContext,
  settingsPageTitle,
  settingsStackKey,
  useSettingsPocketNav,
  type SettingsTarget,
} from "./settings-navigation";
import { SettingsRootPage } from "./settings-root-page";
import { AccountPage } from "./account-page";
import {
  AccountPasswordPage,
  AccountInvoicesPage,
  AccountDeletePage,
} from "./account-subpages";
import { PreferencesPage } from "./preferences-page";
import {
  PreferencesShortcutsPage,
  ShortcutEditPage,
} from "./preferences-shortcuts-page";
import { AppearancePage } from "./preferences-appearance-page";
import { VoicePage } from "./preferences-voice-page";
import { VoiceDictationPromptsPage } from "./voice-dictation-prompts-page";
import { VoiceDictationPromptEditPage } from "./voice-dictation-prompt-edit-page";
import { VoiceVocabularyPage } from "./voice-vocabulary-page";
import { VoiceVocabularyWordPage } from "./voice-vocabulary-word-page";
import { SoundsPage } from "./preferences-sounds-page";
import { ScreenAndPointerPage } from "./preferences-screen-page";
import { PrivacyPage } from "./preferences-privacy-page";
import { UpdatesPage } from "./preferences-updates-page";
import { AiToolsPage } from "./ai-tools-page";
import { ConnectionsPage } from "./connections-page";
import {
  attachBrowserSessionPresentation,
  useBrowserConnectionSession,
} from "@/desktop/browser-connection-session";
import { ConnectionToolPage } from "./connections-tool-page";
import {
  ConnectionAppPage,
  AddConnectionAppPage,
} from "./connections-app-pages";
import { UsagePage } from "./usage-page";
import { PluginsPage } from "./plugins-page";
import { PluginsBrowsePage } from "./plugins-browse-page";
import { PluginDetailPage } from "./plugin-detail-page";
import { ProjectsPage } from "./projects-page";
import { ProjectPage } from "./project-page";
import { FolderPage } from "./project-folder-page";
import { GitPage } from "./project-git-page";
import { DevServerPage } from "./project-dev-server-page";
import { AddFolderPage } from "./project-add-folder-page";
import { SkillsPage } from "./skills-page";
import { SkillPage } from "./skill-page";
import { SkillCustomizePage } from "./skill-customize-page";
import { SkillsBrowsePage } from "./skills-browse-page";
import { MemorySection } from "./memory-section";
import { MemoryProposalPage, MemorySummariesPage } from "./memory-subpages";
import { HelpSection } from "./help-section";
import { ContactSection } from "./contact-section";

export interface SettingsPocketProps {
  open: boolean;
  /** The open pages, root first. */
  stack: readonly SettingsTarget[];
  onStackChange: (stack: readonly SettingsTarget[]) => void;
  onClose: () => void;
  /** The person opened Settings, so it takes the keyboard on open. False for
   *  a page an agent or Denker opened: it takes the keyboard only once the
   *  person presses inside it. */
  focusOnOpen?: boolean;
}

/** Web placement: below the top bar, at the right edge. */
const WEB_EDGE = 8;
function webPocketPlacement() {
  const anchor = document
    .querySelector<HTMLElement>("[data-settings-anchor]")
    ?.getBoundingClientRect();
  const width = Math.min(390, window.innerWidth - WEB_EDGE * 2);
  const maxLeft = Math.max(WEB_EDGE, window.innerWidth - width - WEB_EDGE);
  const left = anchor
    ? Math.min(Math.max(WEB_EDGE, anchor.right - width), maxLeft)
    : maxLeft;
  const top = Math.max(
    WEB_EDGE,
    Math.min(
      anchor ? anchor.bottom + 8 : WEB_EDGE,
      window.innerHeight - WEB_EDGE,
    ),
  );
  return {
    left,
    top,
    maxHeight: Math.max(0, window.innerHeight - top - WEB_EDGE),
  };
}
const ROOT: SettingsTarget = { page: "root" };
const DRAG_EXCLUDE = "button, input, textarea, a, [data-no-drag]";

/** The one page switch (spec §9: one registry drives navigation). */
function SettingsPageView({ target }: { target: SettingsTarget }) {
  switch (target.page) {
    case "account":
      return <AccountPage />;
    case "account-password":
      return <AccountPasswordPage />;
    case "account-invoices":
      return <AccountInvoicesPage />;
    case "account-delete":
      return <AccountDeletePage />;
    case "preferences":
      return <PreferencesPage />;
    case "preferences-shortcuts":
      return <PreferencesShortcutsPage />;
    case "preferences-shortcut-edit":
      return <ShortcutEditPage shortcutId={target.args?.shortcutId ?? ""} />;
    case "preferences-appearance":
      return <AppearancePage />;
    case "preferences-voice":
      return <VoicePage />;
    case "preferences-voice-prompts":
      return <VoiceDictationPromptsPage />;
    case "preferences-voice-prompt-edit":
      return <VoiceDictationPromptEditPage promptId={target.args?.promptId} />;
    case "preferences-voice-vocabulary":
      return <VoiceVocabularyPage />;
    case "preferences-voice-vocabulary-word":
      return <VoiceVocabularyWordPage wordId={target.args?.wordId ?? ""} />;
    case "preferences-sounds":
      return <SoundsPage />;
    case "preferences-screen":
      return <ScreenAndPointerPage />;
    case "preferences-updates":
      return <UpdatesPage />;
    case "preferences-privacy":
      return <PrivacyPage />;
    case "preferences-ai-tools":
      return <AiToolsPage />;
    case "plugins":
      return <PluginsPage />;
    case "plugins-browse":
      return <PluginsBrowsePage />;
    case "plugins-plugin":
      return (
        <PluginDetailPage
          slug={target.args?.slug ?? ""}
          version={target.args?.version ?? ""}
        />
      );
    case "connections":
      return <ConnectionsPage />;
    case "connections-browser":
    case "connections-safari":
      return <ConnectionsPage />;
    case "connections-tool":
      return <ConnectionToolPage tool={target.args?.tool ?? ""} />;
    case "connections-app":
      return <ConnectionAppPage app={target.args?.app ?? ""} />;
    case "connections-add-app":
      return <AddConnectionAppPage />;
    case "connections-usage":
      return <UsagePage />;
    case "projects":
      return <ProjectsPage />;
    case "projects-new":
      return <ProjectPage projectId={null} />;
    case "projects-project":
      return <ProjectPage projectId={target.args?.projectId ?? ""} />;
    case "projects-folder":
      return (
        <FolderPage
          projectId={target.args?.projectId ?? ""}
          folderId={target.args?.folderId ?? ""}
        />
      );
    case "projects-git":
      return (
        <GitPage
          projectId={target.args?.projectId ?? ""}
          folderId={target.args?.folderId ?? ""}
        />
      );
    case "projects-dev-server":
      return (
        <DevServerPage
          projectId={target.args?.projectId ?? ""}
          folderId={target.args?.folderId ?? ""}
        />
      );
    case "projects-add-folder":
      return <AddFolderPage projectId={target.args?.projectId ?? ""} />;
    case "skills":
      return <SkillsPage />;
    case "skills-skill":
      return (
        <SkillPage
          skillId={target.args?.skillId ?? ""}
          kind={target.args?.kind}
        />
      );
    case "skills-customize":
      return <SkillCustomizePage skillId={target.args?.skillId ?? ""} />;
    case "skills-browse":
      return <SkillsBrowsePage />;
    case "memory":
      return <MemorySection />;
    case "memory-proposal":
      return <MemoryProposalPage reviewId={target.args?.reviewId} />;
    case "memory-summaries":
      return <MemorySummariesPage />;
    case "help":
      return <HelpSection />;
    case "help-community":
      return <ContactSection />;
    default:
      return <SettingsRootPage />;
  }
}

export function SettingsPocket({
  open,
  stack,
  onStackChange,
  onClose,
  focusOnOpen = true,
}: SettingsPocketProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Also re-renders the header title (settingsPageTitle) on a language change.
  const t = useT(settingsShellMessages);
  const desktop = isTauri();
  const browserSession = useBrowserConnectionSession((state) => state.session);
  const yielding = browserSession?.yielding ?? false;
  const browserSessionId = browserSession?.id;
  const browserYieldId = browserSession?.yieldId;
  useEffect(
    () => (open && desktop ? attachBrowserSessionPresentation() : undefined),
    [open, desktop],
  );
  useLayoutEffect(() => {
    if (desktop && open) requestNativeGlassRefresh({ trackTransition: false });
  }, [desktop, open, yielding]);
  useLayoutEffect(() => {
    const display = getWindowDisplayId();
    if (desktop && open && yielding && browserSessionId && display !== null)
      return acknowledgeBrowserSettingsYield(
        { id: browserSessionId, yieldId: browserYieldId },
        display,
      );
  }, [desktop, open, yielding, browserSessionId, browserYieldId]);
  const [webPlacement, setWebPlacement] = useState(webPocketPlacement);
  useLayoutEffect(() => {
    if (!open || desktop) return;
    const place = () => setWebPlacement(webPocketPlacement());
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, desktop]);
  const frameSurface = useSettingsFrameSurface(panelRef, open);
  const { position, dragHandleProps } = useDraggableModal({
    open,
    containerRef: panelRef,
  });
  const layer = useInteractiveLayer("settings-pocket", {
    active: open && !yielding,
    baseZIndex: 70,
    tier: "modal",
  });
  const top = stack[stack.length - 1] ?? ROOT;
  const lastDepth = useRef(stack.length);
  const motion =
    stack.length > lastDepth.current
      ? "in"
      : stack.length < lastDepth.current
        ? "out"
        : null;
  useEffect(() => {
    lastDepth.current = stack.length;
  }, [stack.length]);

  const { nav, registerLeaveGuard } = useSettingsPocketNav(
    stack,
    onStackChange,
    onClose,
  );

  useEffect(() => {
    if (!open || !focusOnOpen) return;
    const opener = document.activeElement;
    const panel = panelRef.current;
    panel?.focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      // Keep an intentional focus move to another surface; otherwise return
      // keyboard users to the control that opened Settings.
      if (
        opener instanceof HTMLElement &&
        opener.isConnected &&
        (active === document.body || active === null || panel?.contains(active))
      )
        opener.focus({ preventScroll: true });
    };
  }, [open, focusOnOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (yielding || event.key !== "Escape" || event.defaultPrevented) return;
      // An open value menu closes itself first.
      if ((event.target as HTMLElement).closest('[role="menu"]')) return;
      event.preventDefault();
      event.stopPropagation();
      nav.back();
    },
    [nav, yielding],
  );

  if (!open) return null;

  const framed = desktop && frameSurface.style !== null;
  const headerDragProps = framed
    ? {
        onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
          const panel = panelRef.current;
          if (
            event.button !== 0 ||
            !panel ||
            (event.target as HTMLElement).closest(DRAG_EXCLUDE)
          )
            return;
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          beginSettingsFrameDrag(
            event,
            panel.getBoundingClientRect().height,
            () => captureSettingsFramePreview(panel),
          );
        },
      }
    : dragHandleProps;
  const placement = framed
    ? frameSurface.style
    : position
      ? {
          left: position.x,
          top: position.y,
          maxHeight: `calc(100vh - ${position.y + WEB_EDGE}px)`,
        }
      : webPlacement;

  return createPortal(
    <ModalSurfaceScope>
      <PocketPanel
        ref={panelRef}
        nativeGroup="settings-pocket"
        testId="settings-pocket"
        className="fixed flex w-[390px] max-w-[calc(100vw-16px)] flex-col outline-none"
        style={{
          zIndex: layer.zIndex,
          ...placement,
          ...(yielding ? { visibility: "hidden" } : {}),
        }}
        nativeSurface={!yielding}
        inert={yielding || undefined}
        aria-hidden={yielding || undefined}
        role="dialog"
        aria-label={t("pageRoot")}
        tabIndex={-1}
        data-interactive={yielding ? undefined : "true"}
        onKeyDown={handleKeyDown}
        {...layer.interactionProps}
        onPointerDownCapture={(event) => {
          // A pocket that opened without the keyboard gets it from the
          // person's own press: the overlay does not take it on a click.
          if (desktop && !document.hasFocus()) restoreAndFocusCurrentWindow();
          layer.interactionProps.onPointerDownCapture(event);
        }}
      >
        <PocketHeader
          title={settingsPageTitle(top)}
          onBack={stack.length > 1 ? nav.back : undefined}
          onClose={nav.close}
          testIdPrefix="settings"
          dragHandleProps={headerDragProps}
        />
        <div
          key={settingsStackKey(stack)}
          className={cn(
            "scrollbar-none -mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-1.5",
            top.page === "preferences-shortcuts" &&
              "flex flex-col overflow-hidden",
            motion === "in" && "motion-safe:animate-pocket-in",
            motion === "out" && "motion-safe:animate-pocket-out",
          )}
          data-testid="settings-body"
        >
          <SettingsDraftProvider
            sessionId={desktop ? frameSurface.sessionId : null}
          >
            <SettingsLeaveGuardContext.Provider value={registerLeaveGuard}>
              <SettingsNavContext.Provider value={nav}>
                <ErrorBoundary>
                  {/* Labels and hints are read whole, on more lines when they need them. */}
                  <PocketRowWrapScope>
                    <SettingsPageView target={top} />
                  </PocketRowWrapScope>
                </ErrorBoundary>
              </SettingsNavContext.Provider>
            </SettingsLeaveGuardContext.Provider>
          </SettingsDraftProvider>
        </div>
      </PocketPanel>
    </ModalSurfaceScope>,
    document.body,
  );
}
